// Import Packages
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import RichTextEditor from "react-rte";
import { useSetRecoilState } from 'recoil';

// Import Material UI
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Close from "@material-ui/icons/Close";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';

// Import Pages
import VariableService from "../../../../../../../services/VariableService/VariableService";
import ArrayVariableEditor from "../../../../../../../components/ArrayVariableEditor/ArrayVariableEditor";
import DataTableVariableEditor from "../../../../../../../components/DataTableVariableEditor/DataTableVariableEditor";
import { alertState } from "../../../../../../../recoil-store/recoil-state";
import DictionaryVariableEditor
    from "../../../../../../../components/DictionaryVariableEditor/DictionaryVariableEditor";
import ApplicationService from "../../../../../../../services/ApplicationService/ApplicationService";

// Import Helpers
import { checkSpecialCharacter } from "../../../../../../../helpers/helper";

const useStyles = makeStyles((theme) => ({
    formControl: {
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
}));

function EditVariables(props) {

    const {
        setOpenVariables,
        getByVariables,
        variableTypeList,
        setVariables
    } = props;
    const { t } = useTranslation();
    const [variableName, setVariableName] = useState(getByVariables?.name);
    const [variableDesc, setVariableDesc] = useState(
        RichTextEditor.createValueFromString(getByVariables?.description, "html")
    );
    const [variableType, setVariableType] = useState(getByVariables?.type_id);
    const [variableAs, setVariableAs] = useState();
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [applications, setApplications] = useState([]);
    const [defaultValue, setDefaultValue] = useState('');
    const [displayName, setDisplayName] = useState(getByVariables?.display_name);

    const appConnectorTypeId = '8334e115-cbb3-4254-9453-b9084aed4f67';

    const setAlert = useSetRecoilState(alertState);

    const classes = useStyles();

    useEffect(() => {
        getApplications().then(r => r);
    }, []);

    const getApplications = async () => {
        const response = await ApplicationService.getApplications();
        if (response.status) {
            setApplications(response.data);
        }
    };

    const handleClose = () => {
        setOpenVariables(false);
    };

    function handleChangeType(e) {
        let filterData = variableTypeList.filter(
            (item) => item.id === e.target.value
        );
        if (filterData[0]?.name === "boolean") {
            setDefaultValue(true);
        } else if (
            filterData[0]?.name === "dictionary" ||
            filterData[0]?.name === "array"
        ) {
            setDefaultValue([]);
        } else if (filterData[0]?.name === "data table") {
            setDefaultValue([
                ["", ""],
                ["", ""],
            ]);
        } else if (filterData[0]?.name === "number") {
            setDefaultValue(0);
        } else if (filterData[0]?.name === "object") {
            setDefaultValue('{}');
        } else if (filterData[0]?.name === "app connector") {
            setDefaultValue('');
            setVariableAs('input');
        }
        setVariableType(e.target.value);
        if (variableName.length > 0) setBtnDisabled(false);
        else setBtnDisabled(true);
    }

    //set variableName changed
    function handleChange(e) {
        let name = e.target.value;
        setVariableName(name);
        if (variableType.length > 0 && name.length > 0) setBtnDisabled(false);
        else setBtnDisabled(true);
    }


    // set display name changed
    function changeDisplayName(e) {
        if (e.target.value !== ' ') {
            setDisplayName(e.target.value);
        }
    }

    const handleDelete = async () => {
        const response = await VariableService.deleteVariable(getByVariables.id);
        if (response.status) {
            setVariables(old => [...old.filter(x => x.id !== getByVariables.id)])
        } else {
            // TODO(t("MESSAGES.AUTOMS.VARIABLES-SPEACIAL-CHARACTERS"), response.status);
        }
        setOpenVariables(false);
    };

    useEffect(() => {
        if (getByVariables.is_input) {
            setVariableAs('input');
        } else if (getByVariables.is_output) {
            setVariableAs('output');
        }
        if (variableTypeList) {
            const type = variableTypeList.filter(
                (item) => item.id === getByVariables?.type_id
            );
            if (type.length === 1) {
                setVariableType(type[0].id);
            }
            if (
                type[0]?.name === "dictionary" ||
                type[0]?.name === "array" ||
                type[0]?.name === "boolean"
            ) {
                try {
                    setDefaultValue(getByVariables.default_value ? JSON.parse(getByVariables.default_value) : undefined);
                } catch (error) {
                    setDefaultValue(undefined);
                }
            } else if (type[0]?.name === "data table") {
                setDefaultValue(getByVariables.default_value ? JSON.parse(getByVariables.default_value) : [['', ''], ['', '']]);
            } else {
                setDefaultValue(getByVariables.default_value);
            }
        }
    }, [getByVariables?.type_id, variableTypeList]);

    const handleEdit = async () => {
        if (variableName.length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.AUTOMS.VARIABLE-NAME-CHARACTER-LENGTH-ERROR"),
                severity: 'error'
            }));
        } else if (!checkSpecialCharacter(variableName)) {
            let default_value = '';
            let default_file;
            let sheet_name;
            const filterData = variableTypeList.filter(item => item.id === variableType)
            switch (filterData[0]?.name) {
                case 'string':
                case 'date':
                case 'app connector':
                    default_value = defaultValue;
                    break;
                case 'number':
                case 'boolean':
                    default_value = defaultValue?.toString();
                    break
                case 'data table':
                    if (Array.isArray(defaultValue)) {
                        default_value = JSON.stringify(defaultValue);
                    } else if (defaultValue.file && defaultValue.file.name) {
                        // upload file
                        default_file = defaultValue.file;
                        sheet_name = defaultValue.sheetName ? defaultValue.sheetName : '';

                        // let form_data = new FormData();
                        // for (const key in newData) {
                        //     form_data.append(key, newData[key]);
                        // }
                        // form_data.append("default_file", defaultValue.file);
                        // form_data.append("sheet_name", defaultValue.sheetName ? defaultValue.sheetName : '');
                        // newData = form_data;
                    } else {
                        newData.default_value = JSON.stringify([['', ''], ['', '']]);
                    }
                    break;
                case 'object':
                    if (defaultValue !== '{}') {
                        try {
                            JSON.parse(defaultValue);
                        } catch (error) {
                            setAlert(alert => ({
                                ...alert,
                                show: true,
                                message: t("MESSAGES.AUTOMS.OBJECT-TYPE-IS-NOT-VALID"),
                                severity: 'error'
                            }));
                            return;
                        }
                    }
                    default_value = defaultValue;
                    break;
                default:
                    default_value = JSON.stringify(defaultValue);
                    break;
            }
            let newData = {
                updatingFields: [
                    {
                        field_name: "name",
                        field_value: variableName,
                    },
                    {
                        field_name: "display_name",
                        field_value: displayName,
                    },
                    {
                        field_name: "description",
                        field_value: variableDesc.toString("html"),
                    },
                    {
                        field_name: "type_id",
                        field_value: variableType,
                    },
                    {
                        field_name: "is_input",
                        field_value: variableAs === 'input',
                    },
                    {
                        field_name: "is_output",
                        field_value: variableAs === 'output',
                    },
                    {
                        field_name: "is_customizable",
                        field_value: variableAs !== '',
                    },
                    {
                        field_name: "default_value",
                        field_value: default_value,
                    },
                    {
                        field_name: "autom_version_id",
                        field_value: getByVariables.autom_version_id
                    }
                ],
            };

            if (default_file) {
                let form_data = new FormData();
                form_data.append('updatingFields', JSON.stringify(newData.updatingFields));
                form_data.append("default_file", defaultValue.file);
                form_data.append("sheet_name", defaultValue.sheetName ? defaultValue.sheetName : '');
                newData = form_data;
            }

            const response = await VariableService.updateVariable(
                newData,
                getByVariables.id
            );
            if (response?.status) {
                setVariables(old => [...old.map(x => {
                    if (x.id === getByVariables.id) {
                        let filter = variableTypeList.filter(
                            (item) => item.id === variableType
                        );
                        x.type = filter[0]?.name
                        x.name = variableName;
                        x.display_name = displayName;
                        x.description = variableDesc.toString("html");
                        x.type_id = variableType;
                        x.is_input = variableAs === 'input';
                        x.is_output = variableAs === 'output';
                        x.is_customizable = variableAs !== '';
                        // if (filter[0]?.name === "object") {
                        //     x.default_value = defaultValue;
                        // } else {
                        //     x.default_value = JSON.stringify(defaultValue);
                        // }
                        x.default_value = defaultValue;

                        return x;
                    }
                    return x;
                })])
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.AUTOMS.VARIABLE-SUCCESSFULLY-UPDATED"),
                    severity: 'success'
                }));
                setOpenVariables(false);
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.AUTOMS." + response.message),
                    severity: 'error'
                }));
            }
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.AUTOMS.VARIABLES-SPEACIAL-CHARACTERS"),
                severity: 'error'
            }));
        }
    };

    function defaultValueComponent() {
        let filterData = variableTypeList.filter(
            (item) => item.id === variableType
        );
        if (filterData[0]?.name === "string") {
            return (
                <TextField
                    size="small"
                    style={{ width: "100%" }}
                    id="outlined-basic"
                    variant="outlined"
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    inputProps={{ style: { fontSize: 12 } }}
                />
            );
        } else if (filterData[0]?.name === "number") {
            return (
                <TextField
                    size="small"
                    type="number"
                    style={{ width: "100%" }}
                    id="outlined-basic"
                    variant="outlined"
                    value={defaultValue}
                    onChange={(e) => {
                        if (e.target.value === '') {
                            e.target.value = 0;
                        }
                        setDefaultValue(e.target.value)
                    }}
                    inputProps={{ style: { fontSize: 12 } }}
                />
            );
        } else if (filterData[0]?.name === "boolean") {
            return (
                <div className="mail-server-button-group">
                    <div
                        onClick={() => setDefaultValue(true)}
                        className={
                            defaultValue === true
                                ? "mail-server-button-active"
                                : "mail-server-button"
                        }
                    >
                        {t("CONTENT.AUTOMS-LIST.TRUE")}
                    </div>
                    <div
                        onClick={() => setDefaultValue(false)}
                        className={
                            defaultValue === false
                                ? "mail-server-button-active"
                                : "mail-server-button"
                        }
                    >
                        {t("CONTENT.AUTOMS-LIST.FALSE")}
                    </div>
                </div>
            );
        } else if (filterData[0]?.name === "dictionary") {
            return (
                <DictionaryVariableEditor
                    setData={setDefaultValue}
                    data={defaultValue}
                    titles={["KEY", "VALUE"]}
                    header="DICTIONARY-VARIABLE-EDITOR"
                />
            );
        } else if (filterData[0]?.name === "array") {
            return (
                <ArrayVariableEditor
                    setData={setDefaultValue}
                    data={defaultValue}
                    titles={["VALUE"]}
                    header="DICTIONARY-VARIABLE-EDITOR"
                />
            );
        } else if (filterData[0]?.name === "data table") {
            return (
                <DataTableVariableEditor
                    setData={setDefaultValue}
                    data={defaultValue}
                />
            );
        } else if (filterData[0]?.name === "date") {
            return (
                <TextField type="datetime-local" value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                />
            );
        } else if (filterData[0]?.name === 'object') {
            return (
                <TextField
                    multiline rows={4}
                    size="small"
                    style={{ width: "100%" }}
                    id="outlined-basic"
                    variant="outlined"
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    inputProps={{ style: { fontSize: 12 } }}
                />
            )
        } else if (filterData[0]?.name === 'app connector') {
            return (
                <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    style={{ width: "100%" }}
                >
                    <Select
                        labelId="demo-simple-select-outlined-label"
                        style={{
                            backgroundColor: "#fff",
                            height: "40px",
                            fontSize: "12px",
                        }}
                        id="demo-simple-select-outlined"
                        value={defaultValue}
                        onChange={(e) => setDefaultValue(e.target.value)}
                    >
                        {applications.length > 0 &&
                            applications.map((app) => (
                                <MenuItem
                                    key={app.id}
                                    style={{ fontSize: 12 }}
                                    value={app.id}
                                >
                                    {app.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            )
        }
    }

    const variableAsChange = (event) => {
        setVariableAs(event.target.value);
    }

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            data-test="about-dialog"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="variables-dialog"
            PaperProps={{
                style: {
                    minWidth: 400
                }
            }}
        >
            <DialogTitle id="alert-dialog-title">
                <div id="about-title">
                    {t("CONTENT.AUTOMS-LIST.EDIT-VARIABLES")}
                    <Button data-test="canceldialog-button" onClick={handleClose}>
                        <Close />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent
                id="alert-dialog-description"
                className="dialog-content-area"
            >
                {variableName?.toLowerCase() !== "now" ? <>
                    <span className="dialog-text">
                        {t("CONTENT.AUTOMS-LIST.VARIABLES-TYPE")}
                    </span>
                    <DialogContentText>
                        <FormControl
                            className={classes.formControl}
                            style={{ width: "100%" }}
                        >
                            <Select
                                style={{ height: "40px", fontSize: "12px" }}
                                labelId="demo-simple-select-outlined-label"
                                id="demo-simple-select-outlined"
                                variant="outlined"
                                placeholder="Select Type"
                                value={variableType}
                                onChange={(e) => handleChangeType(e)}
                            >
                                {variableTypeList.length > 0 ? (
                                    variableTypeList.map(
                                        (item) =>
                                            item !== undefined && (
                                                <MenuItem
                                                    style={{ fontSize: 12 }}
                                                    key={item.id}
                                                    value={item.id}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            )
                                    )
                                ) : (
                                    <MenuItem style={{ fontSize: 12 }} />
                                )}
                            </Select>
                        </FormControl>
                    </DialogContentText>

                    <span className="dialog-text">
                        {t("CONTENT.AUTOMS-LIST.VARIABLES-NAME")}
                    </span>
                    <DialogContentText>
                        <TextField
                            size="small"
                            style={{ width: "100%" }}
                            id="outlined-basic"
                            variant="outlined"
                            defaultValue={variableName}
                            onChange={(e) => handleChange(e)}
                            inputProps={{ style: { fontSize: 12 } }}
                        />
                    </DialogContentText>
                    <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.VARIABLES-DISPLAY-NAME")}</span>
                    <DialogContentText>
                        <TextField
                            size="small"
                            style={{ width: "100%" }}
                            id="outlined-basic"
                            variant="outlined"
                            value={displayName}
                            onChange={(e) => changeDisplayName(e)}
                            inputProps={{ style: { fontSize: 12 } }}
                        />
                    </DialogContentText>
                    {variableTypeList.filter((item) => item.id === getByVariables?.type_id)[0]?.name === "app connector" ? (
                        <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.APPLICATION")}</span>
                    ) : variableTypeList.filter((item) => item.id === getByVariables?.type_id)[0]?.name ? (
                        <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.DEFAULT-VALUE")}</span>
                    ) : (
                        <span className="dialog-text" />
                    )}

                    <DialogContentText>
                        {defaultValueComponent()}
                    </DialogContentText>
                    <span className="dialog-text">
                        {t("CONTENT.AUTOMS-LIST.VARIABLES-DESCRIPTION")}
                    </span>
                    <DialogContentText>
                        <RichTextEditor
                            value={variableDesc}
                            editorClassName="publish-editor"
                            onChange={(value) => setVariableDesc(value)}
                        // toolbarConfig={toolbarConfig}
                        />
                    </DialogContentText>
                    <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.SET-AS")}</span>
                    <div className="dialog-set-options">
                        <RadioGroup style={{ marginBottom: '12px' }} row aria-label="position" name="position"
                            value={variableAs} onChange={variableAsChange}>
                            <FormControlLabel value="input" control={<Radio style={{ color: "#C4C4C4" }} />}
                                label="Input" />
                            <FormControlLabel disabled={variableType === appConnectorTypeId} value="output" control={<Radio style={{ color: "#C4C4C4" }} />}
                                label="Output" />
                        </RadioGroup>
                    </div>
                    <DialogContentText>
                        <Button
                            data-test="submitdialog-button"
                            onClick={handleDelete}
                            variant="outlined"
                            style={{
                                float: "left",
                                backgroundColor: "#D75E5E",
                                borderRadius: "2px",
                                color: "#fff",
                                width: "40%",
                                border: "none",
                            }}
                        >
                            {t("CONTENT.AUTOMS-LIST.DELETE")}
                        </Button>

                        <Button
                            data-test="submitdialog-button"
                            onClick={handleEdit}
                            variant="outlined"
                            disabled={btnDisabled || (variableType === appConnectorTypeId && defaultValue === '')}
                            style={{
                                float: "right",
                                backgroundColor: "#46474E",
                                borderRadius: "2px",
                                color: "#fff",
                                width: "40%",
                                border: "none",
                            }}
                        >
                            {t("CONTENT.AUTOMS-LIST.EDIT")}
                        </Button>
                    </DialogContentText> </> : <>
                    <span className="dialog-text">
                        {t("CONTENT.AUTOMS-LIST.VARIABLES-DESCRIPTION")}
                    </span>
                    <DialogContentText>
                        <RichTextEditor
                            value={variableDesc}
                            editorClassName="publish-editor"
                            disabled={true}
                            onChange={(value) => setVariableDesc(value)}
                        // toolbarConfig={toolbarConfig}
                        />
                    </DialogContentText>
                    <DialogContentText>
                        <Button
                            data-test="submitdialog-button"
                            onClick={handleClose}
                            variant="outlined"
                            style={{
                                float: "right",
                                backgroundColor: "#46474E",
                                borderRadius: "2px",
                                color: "#fff",
                                width: "40%",
                                border: "none",
                            }}
                        >
                            {t("CONTENT.AUTOMS-LIST.CLOSE")}
                        </Button>
                    </DialogContentText>
                </>}
            </DialogContent>
        </Dialog>
    );
}

export default EditVariables;
